// Törzsadat-írás: csoportok és munkakörök.
//
// A szerződést a Flutter `groups_repo.dart` és `job_title_repo.dart` adja, mert
// a két felület UGYANAZT az adatot írja — bármelyik eltérés csendben elrontaná
// a másikat:
//
//   projects/{companyId}/groups/{id}     { id, name, userIds[] }
//   projects/{companyId}/jobTitle/{id}   { id, name }
//
// Két részlet, ami nem magától értetődő, de a Flutterből következik:
//
//  1. Az `id` mező a DOKUMENTUMBAN IS benne van, nem csak a dokumentum
//     azonosítójaként. A Flutter modellek `id`-t olvasnak a JSON-ből, és ha ez
//     hiányzik, a szerkesztés új elemet hozna létre a meglévő módosítása
//     helyett.
//  2. Új elemnél előbb kérünk egy üres dokumentum-hivatkozást, hogy az
//     azonosító már az írás előtt meglegyen — ugyanúgy, ahogy a Flutter teszi
//     (`collectionRef.doc()` majd `set`).
//
// A `jobTitle` kollekció neve SZÁNDÉKOSAN egyes számú: így hívja az éles adat.

import {
  collection,
  deleteDoc,
  deleteField,
  doc,
  getDoc,
  setDoc,
  updateDoc,
  type Firestore,
} from 'firebase/firestore';

import { t } from './i18n';

export type MasterDataContext = {
  /// A kapcsolat kívülről jön, nem globális szingleton — így az írási réteg
  /// emulátor ellen is futtatható, valódi adat érintése nélkül.
  db: Firestore;
  companyId: string;
};

export type GroupInput = {
  /// `undefined` új csoportnál; meglévőnél a dokumentum azonosítója.
  id?: string;
  name: string;
  userIds: string[];
};

export type JobTitleInput = {
  id?: string;
  name: string;
};

/// Üres nevet nem engedünk menteni.
///
/// Nem csak kényelmi kérdés: névtelen csoport a kiosztó felületen
/// megkülönböztethetetlen lenne a többitől.
function requireName(name: string): string {
  const trimmed = name.trim();
  if (trimmed.length === 0) {
    throw new Error(t('general.nameRequired'));
  }
  return trimmed;
}

/// Csoport létrehozása vagy módosítása. Visszaadja a csoport azonosítóját.
export async function saveGroup(
  ctx: MasterDataContext,
  group: GroupInput,
): Promise<string> {
  const name = requireName(group.name);
  // Ismétlődő azonosító a tagok között felesleges írás, és a Flutter oldalon
  // duplán jelenítené meg ugyanazt az embert.
  const userIds = [...new Set(group.userIds)];

  const groups = collection(ctx.db, 'projects', ctx.companyId, 'groups');

  if (group.id === undefined) {
    const ref = doc(groups);
    await setDoc(ref, { id: ref.id, name, userIds });
    return ref.id;
  }

  await updateDoc(doc(groups, group.id), { name, userIds });
  return group.id;
}

export async function deleteGroup(
  ctx: MasterDataContext,
  id: string,
): Promise<void> {
  await deleteDoc(doc(ctx.db, 'projects', ctx.companyId, 'groups', id));
}

/// Munkakör létrehozása vagy módosítása. Visszaadja az azonosítóját.
export async function saveJobTitle(
  ctx: MasterDataContext,
  jobTitle: JobTitleInput,
): Promise<string> {
  const name = requireName(jobTitle.name);
  const jobTitles = collection(ctx.db, 'projects', ctx.companyId, 'jobTitle');

  if (jobTitle.id === undefined) {
    const ref = doc(jobTitles);
    await setDoc(ref, { id: ref.id, name });
    return ref.id;
  }

  await updateDoc(doc(jobTitles, jobTitle.id), { name });
  return jobTitle.id;
}

export async function deleteJobTitle(
  ctx: MasterDataContext,
  id: string,
): Promise<void> {
  await deleteDoc(doc(ctx.db, 'projects', ctx.companyId, 'jobTitle', id));
}

/// A határidő-emlékeztető mentése a felhasználó dokumentumára.
///
/// A mező a Flutter oldallal KÖZÖS: `users/{uid}.autoUserPush`, a
/// `{ day, hours, minutes, pushAfterDeadline }` alakkal (lásd
/// `lib/pages/users/models/user_model.dart` `AutoUserPushModel`). Nem saját
/// szerkezetet írunk: a mobil ugyanezt olvassa, és a cloud function ebből
/// dönti el, mikor szóljon.
///
/// `merge: true`, mert a dokumentumon a felhasználó MINDEN adata ott van —
/// teljes felülírással a neve, a cégtagsága és a tokenjei is eltűnnének.
export async function saveAutoUserPush(
  db: Firestore,
  userId: string,
  settings: {
    day: number;
    hours: number;
    minutes: number;
    pushAfterDeadline: boolean;
  },
): Promise<void> {
  await setDoc(
    doc(db, 'users', userId),
    {
      autoUserPush: {
        day: Math.max(0, Math.trunc(settings.day)),
        hours: Math.max(0, Math.trunc(settings.hours)),
        minutes: Math.max(0, Math.trunc(settings.minutes)),
        pushAfterDeadline: settings.pushAfterDeadline,
      },
    },
    { merge: true },
  );
}

/// Dinamikus mező létrehozása vagy módosítása. Visszaadja az azonosítóját.
///
/// A dokumentum alakja a Flutter `FieldModel`-t követi:
///   { id, name, type, isType, isOnMainScreen, status }
/// Az `isType: false` SZÁNDÉKOS és kötelező: ugyanebben a kollekcióban élnek
/// a feladat-TÍPUSOK is (`isType: true`), és a mobil ez alapján válogat. A
/// mező nélkül az új mező típusként jelenne meg a feladatfelvitelnél.
///
/// A `status` azt mondja meg, melyik státuszba lépéshez kötelező a kitöltés.
/// Ha nincs ilyen, a mezőt TÖRÖLJÜK a dokumentumról — `null`-lal a Flutter
/// oldali `TaskStatus` értelmezése elszállna.
export async function saveField(
  ctx: MasterDataContext,
  field: {
    id?: string;
    name: string;
    type: 'text' | 'number' | 'date';
    showOnList: boolean;
    requiredInStatus?: string;
  },
): Promise<string> {
  const name = requireName(field.name);
  const fields = collection(ctx.db, 'projects', ctx.companyId, 'fields');

  const common = {
    name,
    type: field.type,
    isType: false,
    isOnMainScreen: field.showOnList,
  };

  if (field.id === undefined) {
    const ref = doc(fields);
    // Az `id` a dokumentumban IS ott van: a Flutter modell így olvassa.
    // A `status` kulcs csak akkor kerül bele, ha van értéke — `null`-lal a
    // Flutter oldali `TaskStatus` értelmezése elszállna.
    await setDoc(ref, {
      id: ref.id,
      ...common,
      ...(field.requiredInStatus ? { status: field.requiredInStatus } : {}),
    });
    return ref.id;
  }

  // Módosításnál a `deleteField()` az egyetlen helyes út: ha a felhasználó
  // KIVETTE a kötelezőséget, a mezőnek el kell tűnnie a dokumentumról, nem
  // üres értékkel ott maradnia.
  await updateDoc(doc(fields, field.id), {
    ...common,
    status: field.requiredInStatus ?? deleteField(),
  });
  return field.id;
}

export async function deleteFieldDefinition(
  ctx: MasterDataContext,
  id: string,
): Promise<void> {
  await deleteDoc(doc(ctx.db, 'projects', ctx.companyId, 'fields', id));
}

/// Meglévő felhasználó adatainak mentése.
///
/// Amit ÍR: a nevet, a munkakört, a cégen belüli szerepkört és a
/// csoporttagságot. Amit NEM ír: az e-mail címet. Az a BEJELENTKEZÉSI
/// azonosító is egyben (Firebase Auth), és a Firestore-ban átírva a két
/// oldal némán szétcsúszna — a felhasználó a régi címével lépne be, a
/// felületen meg az újat látná. A szerkesztő súgója ezt ki is mondja.
///
/// A szerepkör a `users/{uid}.projects[]` tömb EBBEN a cégben lévő elemén
/// él. A tömböt teljes egészében visszaírjuk, mert a Firestore nem tud
/// tömbelemet a helyén módosítani — de CSAK az ide tartozó elemet cseréljük,
/// a többi cégtagság érintetlen marad.
export async function saveUserProfile(
  db: Firestore,
  companyId: string,
  userId: string,
  input: {
    firstName: string;
    lastName: string;
    jobTitle?: string;
    role: string;
  },
): Promise<void> {
  const ref = doc(db, 'users', userId);
  const snapshot = await getDoc(ref);
  const memberships = Array.isArray(snapshot.data()?.['projects'])
    ? (snapshot.data()!['projects'] as { id?: unknown; role?: unknown }[])
    : [];

  const next = memberships.map((membership) =>
    membership.id === companyId ? { ...membership, role: input.role } : membership,
  );
  if (!next.some((membership) => membership.id === companyId)) {
    next.push({ id: companyId, role: input.role });
  }

  await setDoc(
    ref,
    {
      firstName: input.firstName,
      lastName: input.lastName,
      // Üres munkakörnél a mezőt TÖRÖLJÜK: üres string a listákban egy
      // láthatatlan, de létező munkakörként viselkedne.
      jobTitle: input.jobTitle && input.jobTitle.length > 0 ? input.jobTitle : deleteField(),
      projects: next,
    },
    { merge: true },
  );
}

/// A felhasználó csoporttagságának átvezetése.
///
/// A tagság a CSOPORT dokumentumán él (`userIds`), nem a felhasználóén,
/// ezért csoportonként kell írni — de CSAK ott, ahol tényleg változott.
/// Enélkül minden mentés minden csoportot újraírna, és a változásnapló tele
/// lenne tartalom nélküli bejegyzésekkel.
export async function syncUserGroups(
  ctx: MasterDataContext,
  userId: string,
  groups: { id: string; userIds: string[] }[],
  groupIds: string[],
): Promise<void> {
  const wanted = new Set(groupIds);

  for (const group of groups) {
    const isMember = group.userIds.includes(userId);
    const shouldBeMember = wanted.has(group.id);
    if (isMember === shouldBeMember) continue;

    const userIds = shouldBeMember
      ? [...group.userIds, userId]
      : group.userIds.filter((id) => id !== userId);

    await updateDoc(doc(ctx.db, 'projects', ctx.companyId, 'groups', group.id), { userIds });
  }
}
